import ExportAuditTrail from '@Application/AuditTrail/Queries/ExportAuditTrail';
import exportAuditTrail from '@Application/AuditTrail/exportAuditTrail';
import PageContainer from '@Guidelines/Layout/PageContainer';
import PageHeader from '@Guidelines/Layout/PageHeader';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import AuditTrailContent from '@UI/Pages/Administration/AuditTrail/AuditTrailContent';
import AuditTrailNotifications from '@UI/Pages/Administration/AuditTrail/AuditTrailNotifications';
import Filters from '@UI/Pages/Administration/AuditTrail/Filters';
import auditTrailFiltersSignal from '@UI/Pages/Administration/AuditTrail/Filters/Signals/auditTrailFiltersSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { Download } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const EXPORT_DISABLED_TOOLTIP = 'Choisissez une période pour exporter (92 jours au maximum).';

/**
 * Assembles the page: header (with the screen's only action, "Exporter"), `Filters`, the
 * data-owning `AuditTrailContent` and notifications, in that visual order. `Filters` cannot fetch
 * its own `users`/`facilities` — it is a plain, prop-driven component — so this page supplies them
 * from the very same sources `AuditTrailContent` itself populates, through a plain `useSelector`
 * (never a second `useStoreQuery`: `AuditTrailContent` already owns those fetches, this only reads
 * the values they populate).
 *
 * The export button carries no permission gate of its own: the page is already closed behind
 * `AUDIT_READ` by the route guard, and a second, identical gate on the button would only suggest a
 * distinct right that does not exist. It stays disabled until both bounds of the period are chosen —
 * the export endpoint requires the period, unlike the list itself.
 */
const AuditTrailPage: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const filters = useSignalValue(auditTrailFiltersSignal);
	const [isExporting, setIsExporting] = React.useState(false);

	const usersList = useSelector((state) => state.usersStore.users);
	const facilitiesList = useSelector((state) => state.accountFacilitiesStore.accountFacilities);

	const users = React.useMemo(() => usersList?.list ?? null, [usersList]);
	const facilities = React.useMemo(() => facilitiesList?.list ?? null, [facilitiesList]);

	const handleExportClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		setIsExporting(true);
		dispatch(exportAuditTrail(new ExportAuditTrail(accountId, filters.queryParams)))
			.catch(ignoreHandledError)
			.finally(() => setIsExporting(false));
	}, [dispatch, accountId, filters]);

	return (
		<PageContainer>
			<PageHeader
				eyebrow="CONFIGURATION"
				title="Journal d'audit"
				description="Qui a fait quoi, sur quoi, quand — consultation en lecture seule, sans aucune action de modification."
				actions={
					<Button
						type="button"
						label="Exporter"
						icon={<Download size={16} />}
						loading={isExporting}
						disabled={!filters.hasCompletePeriod || isExporting}
						tooltip={!filters.hasCompletePeriod ? EXPORT_DISABLED_TOOLTIP : undefined}
						tooltipOptions={{ showOnDisabled: true }}
						onClick={handleExportClick}
					/>
				}
			/>
			<Filters users={users} facilities={facilities} />
			<AuditTrailContent />
			<AuditTrailNotifications />
		</PageContainer>
	);
};

export default AuditTrailPage;
